import {
  PublicClientApplication,
  type Configuration,
  type PopupRequest,
} from '@azure/msal-browser'
import type { AuthRuntimeConfig } from './authConfig'

/**
 * Build and initialize an MSAL instance from runtime config. Returns null
 * when auth is disabled (Entra not configured) so the app can run open in
 * dev/demo mode.
 */
export async function createMsalInstance(
  cfg: AuthRuntimeConfig,
): Promise<PublicClientApplication | null> {
  if (!cfg.authEnabled || !cfg.spaClientId || !cfg.authority) return null

  const msalConfig: Configuration = {
    auth: {
      clientId: cfg.spaClientId,
      authority: cfg.authority,
      // Redirect flow returns to the app origin, where handleRedirectPromise()
      // (in AuthProvider) completes sign-in. We use redirect (not popup) auth
      // because popup auth polls popupWindow.closed, which the browser blocks
      // under Cross-Origin-Opener-Policy — that broke staff sign-in with
      // "user_cancelled". Redirect is COOP-immune (no popup, no window.opener).
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  }

  const instance = new PublicClientApplication(msalConfig)
  await instance.initialize()
  return instance
}

/** The scopes needed to obtain an access token for our API. */
export function apiTokenRequest(cfg: AuthRuntimeConfig): PopupRequest {
  return { scopes: cfg.apiScope ? [cfg.apiScope] : [] }
}

/** Login scopes: OIDC basics; API access is acquired separately. */
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email'],
}
